/**
 * Pure decision logic for the online-recovery probe (see `useOnlineRecovery`).
 *
 * Background: the live-update loop depends on react-query polling, which pauses
 * all queries while `onlineManager` reports offline. On low-RAM mobile the OS
 * freezes the web process when backgrounded; on resume the browser sometimes
 * never delivers the `online` event, so `onlineManager` stays stuck offline and
 * every query remains `paused` — auto-update is dead until a manual reload.
 * (Confirmed by a field report: on resume `rqOnline:false`, `paused:8`,
 * `fetching:0` while NetInfo reported a healthy 4g/10Mbps link.)
 */

/** Lifecycle events after which connectivity should be re-checked. */
export const ONLINE_RECHECK_EVENTS = [
  'visibilitychange',
  'resume',
  'pageshow',
  'online',
] as const

/**
 * Whether to run a connectivity probe in response to a lifecycle event.
 *
 * Probe only when react-query currently believes it is OFFLINE (its queries are
 * paused → auto-update is stuck) and the page has just regained visibility
 * after a resume — or the browser fired its own `online` event (which can
 * arrive late, or not at all, after a freeze). Gating on "offline" keeps this a
 * no-op on every healthy resume.
 */
export const shouldProbeForOnline = (
  eventType: string,
  visibilityState: string,
  reactQueryOnline: boolean
): boolean =>
  !reactQueryOnline &&
  (ONLINE_RECHECK_EVENTS as readonly string[]).includes(eventType) &&
  (eventType === 'online' || visibilityState === 'visible')

/**
 * Whether to probe when react-query's onlineManager transitions to offline.
 *
 * The lifecycle-event probe (`shouldProbeForOnline`) can miss the real case:
 * the resume/visibilitychange events often fire while onlineManager still
 * reports online, and it only flips to offline a few hundred ms later — by
 * which point no probe-triggering event fires. Reacting to the offline
 * transition itself, while the page is visible, closes that race. We act only
 * on the offline edge (`online === false`); the probe re-confirms connectivity
 * and only forces online back when the server is actually reachable.
 */
export const shouldProbeOnOfflineTransition = (
  online: boolean,
  visibilityState: string
): boolean => !online && visibilityState === 'visible'
